import { Icon } from '@/components'
import Tag from '@/components/Tag'
import clsx from 'clsx'

export type CostCategoryMode =
  | 'negative'
  | 'low'
  | 'medium'
  | 'high'
  | 'adjustment'
  | 'off'

export interface CostCategoryModeCardProps {
  mode: CostCategoryMode
  title?: string
  badgeText?: string
  temperature?: string
  onClick?: () => void
  className?: string
}

const modeConfigs = {
  negative: {
    borderColor: 'border-base-green-900',
    textColor: 'text-base-green-900',
    bgColor: 'bg-base-green-100',
    badgeColor: 'blue-100',
    iconName: 'ArrowDown' as const,
    defaultTitle: 'Negative Cost Hour',
  },
  low: {
    borderColor: 'border-themed-green-700',
    textColor: 'text-themed-green-700',
    bgColor: 'bg-themed-green-100',
    badgeColor: 'blue-100',
    iconName: 'ArrowDown' as const,
    defaultTitle: 'Low Cost Hour',
  },
  medium: {
    borderColor: 'border-base-orange-500',
    textColor: 'text-base-orange-500',
    bgColor: 'bg-base-orange-100',
    badgeColor: 'blue-100',
    iconName: 'ArrowRight' as const,
    defaultTitle: 'Medium Cost Hour',
  },
  high: {
    borderColor: 'border-base-red-500',
    textColor: 'text-base-red-500',
    bgColor: 'bg-base-red-100',
    badgeColor: 'blue-100',
    iconName: 'ArrowUp' as const,
    defaultTitle: 'High Cost Hour',
  },
  adjustment: {
    borderColor: 'border-base-blue-500',
    textColor: 'text-base-blue-500',
    bgColor: 'bg-base-blue-100',
    badgeColor: 'blue-100',
    iconName: 'Thermometer' as const,
    defaultTitle: 'Thermostat adjustment made',
  },
  off: {
    borderColor: 'border-base-grey-300',
    textColor: 'text-base-grey-500',
    bgColor: 'bg-base-grey-100',
    badgeColor: 'grey-100',
    iconName: 'MinusCircle' as const,
    defaultTitle: 'Cost Categories Off',
  },
} as const

export default function CostCategoryModeCard({
  mode,
  title,
  badgeText,
  temperature,
  onClick,
  className = '',
}: CostCategoryModeCardProps) {
  const config = modeConfigs[mode]
  const displayTitle = title || config.defaultTitle
  const displayTemperature = temperature ? `${temperature}°` : null

  const cardClasses = clsx(
    'flex items-center justify-between p-4 rounded-xl border border-solid',
    config.borderColor,
    {
      'cursor-pointer hover:shadow-sm transition-shadow': onClick,
    },
    className,
  )

  const content = (
    <>
      <div className="flex items-center">
        <div
          className={clsx(
            'mr-2 p-2 flex justify-center items-center rounded-lg',
            config.textColor,
            config.bgColor,
          )}
        >
          <Icon name={config.iconName} size={20} />
        </div>
        <div className="flex flex-col gap-1">
          <div className={clsx('text-sm font-bold', config.textColor)}>
            {displayTitle}
          </div>
          {badgeText && (
            <Tag
              color={config.badgeColor}
              className={clsx(
                'h-5 text-xs font-bold w-fit',
                mode === 'off'
                  ? 'text-themed-grey-500'
                  : 'text-themed-blue-500',
              )}
            >
              {badgeText}
            </Tag>
          )}
        </div>
      </div>
      {displayTemperature && (
        <div className={`text-xl font-bold`}>{displayTemperature}</div>
      )}
    </>
  )

  if (onClick) {
    return (
      <button className={cardClasses} onClick={onClick} type="button">
        {content}
      </button>
    )
  }

  return <div className={cardClasses}>{content}</div>
}
